import { Logger } from '@gitbook/util-debug';
import { assertIsDefined, assertIsUndefined, assertNever } from '@gitbook/util-ts';

import { indexTree, lookupTreeDescendants, type TreeStrategy } from './indexTree';
import { mergeKeys } from './mergeKeys';

const logger = Logger('mergeTrees');

/**
 * Case where the item was modified in both branches
 * Note: ancestor can be undefined if the item was added in both branches
 * but did not exist in ancestor.
 */
export interface MergeItemsCaseConflictModified<Item> {
    case: 'conflict-both-modified';
    /** Original base item */
    base: Item;
    /** Original head item */
    head: Item;
    /** Original ancestor item */
    ancestor: Item | undefined;
    /** Children from the merge between head and base. */
    children: Item[];
}

/**
 * Case where the item was deleted in the head branch and modified in the base branch.
 */
export interface MergeItemsCaseConflictDeletedInHead<Item> {
    case: 'conflict-deleted-in-head';
    /** Original base item */
    base: Item;
    /** Original ancestor item */
    ancestor: Item;
    /** If true, the base node itself has been modified from ancestor. */
    isBaseModified: boolean;
    /** Children of the item in the base branch after the merge with ancestor. */
    children: Item[];
}

/**
 * Case where the item was deleted in the base branch and modified in the head branch.
 */
export interface MergeItemsCaseConflictDeletedInBase<Item> {
    case: 'conflict-deleted-in-base';
    /** Original head item */
    head: Item;
    /** Original ancestor item */
    ancestor: Item;
    /** If true, the head node itself has been modified from ancestor. */
    isHeadModified: boolean;
    /** Children of the item in the head branch after the merge with ancestor. */
    children: Item[];
}

export type MergeItemsConflictCase<Item> =
    | MergeItemsCaseConflictModified<Item>
    | MergeItemsCaseConflictDeletedInHead<Item>
    | MergeItemsCaseConflictDeletedInBase<Item>;

/**
 * All cases the merge engine resolves automatically — i.e. where no conflict is raised.
 * - these are recorded in `result.changes` for downstream visibility
 * - `mergeConflicts` is never invoked for them and `isMergeConflictCase` returns false
 * - each variant below documents the user-facing scenario + rationale for treating it as non-conflicting
 */
export type MergeItemsNonConflictCase<Item> =
    | {
          /**
           * Not a conflict, both branches deleted the item.
           */
          case: 'deleted-in-both';
          ancestor: Item;
      }
    | {
          /**
           * Not a conflict, as the item was only modified in base.
           */
          case: 'modified-in-base';
          ancestor: Item;
          base: Item;
      }
    | {
          /**
           * Not a conflict, as the merge was only modified in head.
           */
          case: 'modified-in-head';
          ancestor: Item;
          head: Item;
      }
    | {
          /**
           * Not a conflict despite being modified in both. This happens if both branches modified the item but our item comparison strategy reported them equivalent (e.g. both users bolded the same word).
           */
          case: 'modified-in-both';
          ancestor: Item;
          base: Item;
          head: Item;
      }
    | {
          /**
           * Not a conflict, as only the base branch added the item; it didn't exist in ancestor or head.
           */
          case: 'added-in-base';
          base: Item;
      }
    | {
          /**
           * Not a conflict, as only the head branch added the item; it didn't exist in ancestor or base.
           */
          case: 'added-in-head';
          head: Item;
      }
    | {
          /**
           * Not a conflict, as only the base branch deleted the item; head left it untouched (still equal to ancestor).
           */
          case: 'deleted-in-base';
          ancestor: Item;
      }
    | {
          /**
           * Not a conflict, as only the head branch deleted the item; base left it untouched (still equal to ancestor).
           */
          case: 'deleted-in-head';
          ancestor: Item;
      }
    | {
          /**
           * Not a conflict, as both branches added the same item AND `strategy.compareItems(base, head)` reported them equivalent.
           */
          case: 'added-in-both';
          base: Item;
          head: Item;
      };

export type MergeItemsCase<Item> = MergeItemsConflictCase<Item> | MergeItemsNonConflictCase<Item>;

/**
 * Merge two tree items.
 */
export type MergeConflictHandler<Item> = (mergeCase: MergeItemsConflictCase<Item>) => {
    /**
     * The items that should be added to the document as part of this merge.
     */
    items: Item[];

    /**
     * Optionally the handler can change the type of the merge.
     *
     * For example, a handler might decide to resolve a conflict by
     * choosing the base version of the item. In this case, it should
     * return the correct merge case.
     */
    case?: MergeItemsCase<Item>;
};

export interface MergeStrategy<Item> extends TreeStrategy<Item> {
    /**
     * Merge two tree items.
     */
    mergeConflicts: MergeConflictHandler<Item>;
}

export interface MergeResult<Item> {
    items: Item[];

    /**
     * Map of changes that were applied to the base tree.
     * If empty, it means the trees were not modified (base = head = ancestor)
     */
    changes: Map<string, MergeItemsCase<Item>>;
}

/**
 * 3-way trees merge using a fully customizable strategy.
 * The strategy can be used to maintain all nodes (deleted, added) and to potentially generate more nodes.
 */
export function mergeTrees<Item>(
    trees: {
        ancestor: Item[];
        base: Item[];
        head: Item[];
    },
    strategy: MergeStrategy<Item>
): MergeResult<Item> {
    if (trees.ancestor.length === 0 && trees.base.length === 0 && trees.head.length === 0) {
        return { items: [], changes: new Map() };
    }

    const ancestorIndex = indexTree(trees.ancestor, strategy);
    const baseIndex = indexTree(trees.base, strategy);
    const headIndex = indexTree(trees.head, strategy);

    const changes = new Map<string, MergeItemsCase<Item>>();

    /**
     * Recrusively merge a level of the tree (children of `parent`).
     * The approach is:
     *   - Build an ordered set of keys from all 3 versions (ancestor, base, head): all keys that are potentially touched between head and base and ancestor.
     *   - For each key, evaluate what base/head did on this key
     *   - For each key, also evaluate the merge between the children
     *   - Push the result to the output, and make the change in `changes`
     */
    const mergeTreeLevel = (
        parent: string | undefined,
        options: {
            inDeletedAncestor?: 'base' | 'head';
        } = {}
    ): Item[] => {
        const { inDeletedAncestor } = options;
        const ancestor = parent
            ? lookupTreeDescendants(strategy, ancestorIndex, parent)
            : trees.ancestor;
        const base = parent ? lookupTreeDescendants(strategy, baseIndex, parent) : trees.base;
        const head = parent ? lookupTreeDescendants(strategy, headIndex, parent) : trees.head;

        // Build an ordered set of keys from all 3 versions
        // trying to preserve the order between the versions as best as possible
        const keys = mergeKeys(
            (ancestor || []).map((item) => strategy.getId(item)),
            (base || []).map((item) => strategy.getId(item)),
            (head || []).map((item) => strategy.getId(item))
        );
        const items: Item[] = [];

        /**
         * Push items to the output, and mark them as globally processed in the tree,
         * to avoid duplicated elements.
         */
        const pushItems = (toPush: Item[]) => {
            toPush.forEach((item) => {
                items.push(item);
            });
        };

        /**
         * Push an item to the output, and mark it as changed.
         */
        const pushItemChange = (item: Item, change: MergeItemsNonConflictCase<Item>) => {
            pushItems([item]);
            changes.set(strategy.getId(item), change);
        };

        /**
         * Handle a conflict on an element and push the result to the output.
         */
        const pushItemConflict = (key: string, mergeCase: MergeItemsConflictCase<Item>) => {
            const result = strategy.mergeConflicts(mergeCase);

            pushItems(result.items);
            changes.set(key, result.case || mergeCase);
        };

        /**
         * Push a potentially modified item to the output, and mark it as changed.
         */
        const pushItemModification = (
            key: string,
            ancestorItem: Item,
            baseItem: Item,
            headItem: Item
        ) => {
            const isBaseModified = strategy.compareItems(baseItem, ancestorItem) !== 0;
            const isHeadModified = strategy.compareItems(headItem, ancestorItem) !== 0;
            const children = mergeTreeLevel(key);

            if (isBaseModified && isHeadModified) {
                if (strategy.compareItems(baseItem, headItem) === 0) {
                    // Both sides converged but to the same result, so we don't have a conflict. Same shape as the 'added-in-both' handling below.
                    logger.debug('Use equivalent modification in both branches', key, 'in', parent);
                    pushItemChange(strategy.setChildren(headItem, children), {
                        case: 'modified-in-both',
                        ancestor: ancestorItem,
                        base: baseItem,
                        head: headItem,
                    });
                } else {
                    // Item itself has been modified differently in both branches
                    pushItemConflict(key, {
                        case: 'conflict-both-modified',
                        base: baseItem,
                        head: headItem,
                        ancestor: ancestorItem,
                        children,
                    });
                }
            } else if (isBaseModified) {
                // Item has been modified in base branch only
                logger.debug('Use modified base', key, 'in', parent);
                pushItemChange(strategy.setChildren(baseItem, children), {
                    case: 'modified-in-base',
                    ancestor: ancestorItem,
                    base: baseItem,
                });
            } else if (isHeadModified) {
                // Item has been modified in head branch only
                logger.debug('Use modified head', key, 'in', parent);
                pushItemChange(strategy.setChildren(headItem, children), {
                    case: 'modified-in-head',
                    ancestor: ancestorItem,
                    head: headItem,
                });
            } else {
                // Item has not been modified in both branches, we can safely use the ancestor
                pushItems([strategy.setChildren(ancestorItem, children)]);
            }
        };

        for (const keyChange of keys) {
            const { key } = keyChange;

            if (keyChange.op === '-' && keyChange.move) {
                // Ignore case of the deletion in a move since we are going to handle the addition part.
                continue;
            }

            const ancestorNode = ancestorIndex.get(key);
            const baseNode = baseIndex.get(key);
            const headNode = headIndex.get(key);

            const ancestorItem =
                ancestorNode && ancestorNode?.parent === parent ? ancestorNode.item : undefined;
            const baseItem = baseNode && baseNode?.parent === parent ? baseNode.item : undefined;
            const headItem = headNode && headNode?.parent === parent ? headNode.item : undefined;

            if (baseItem && headItem && !ancestorItem) {
                const areBothModified = strategy.compareItems(baseItem, headItem) !== 0;
                const children = mergeTreeLevel(key);
                if (areBothModified) {
                    // Item was added in both branches but with different data
                    pushItemConflict(key, {
                        case: 'conflict-both-modified',
                        ancestor: undefined,
                        base: baseItem,
                        head: headItem,
                        children,
                    });
                } else {
                    // Item was added in both branches but with the same data
                    logger.debug('Add item in both branches', key, 'in', parent);
                    pushItemChange(strategy.setChildren(headItem, children), {
                        case: 'added-in-both',
                        base: baseItem,
                        head: headItem,
                    });
                }
            } else if (!baseItem && !headItem && ancestorItem) {
                // Item was deleted in both branches
                // We safely ignore it
                logger.debug('Item was deleted in both branches', key, 'in', parent);
                changes.set(key, { case: 'deleted-in-both', ancestor: ancestorItem });
            } else if (ancestorItem && baseItem && headItem) {
                pushItemModification(key, ancestorItem, baseItem, headItem);
            } else if (ancestorItem && !headItem) {
                // Item was deleted in head branch
                assertIsDefined(baseItem);

                const isMoved = Boolean(headNode);
                const isBaseTreeModified = !compareTrees([ancestorItem], [baseItem], strategy);

                if (isMoved) {
                    // If the item was moved in head somewhere else, we do a noop
                    // and the item will be processed later on
                } else if (inDeletedAncestor === 'head' || isBaseTreeModified) {
                    // Item was deleted in head branch but from a previous ancestor
                    if (isBaseTreeModified) {
                        pushItemConflict(key, {
                            case: 'conflict-deleted-in-head',
                            base: baseItem,
                            ancestor: ancestorItem,
                            isBaseModified: strategy.compareItems(baseItem, ancestorItem) !== 0,
                            children: mergeTreeLevel(key, { inDeletedAncestor: 'head' }),
                        });
                    } else {
                        pushItems([baseItem]);
                    }
                } else {
                    // Delete it because it was unchanged in base branch
                    logger.debug(
                        'Deleted as deleted in head and not modified in base',
                        key,
                        'in',
                        parent
                    );
                    changes.set(key, { case: 'deleted-in-head', ancestor: ancestorItem });
                }
            } else if (ancestorItem && !baseItem) {
                // Item was deleted in base branch
                assertIsDefined(headItem);

                const isMoved = Boolean(baseNode);
                const isHeadTreeModified = !compareTrees([ancestorItem], [headItem], strategy);

                if (isMoved) {
                    // If the item was moved in base somewhere else, we do a noop
                    // and the item will be processed later on
                } else if (inDeletedAncestor === 'base' || isHeadTreeModified) {
                    // Item was deleted in head branch but from a previous ancestor
                    if (isHeadTreeModified) {
                        pushItemConflict(key, {
                            case: 'conflict-deleted-in-base',
                            head: headItem,
                            ancestor: ancestorItem,
                            isHeadModified: strategy.compareItems(headItem, ancestorItem) !== 0,
                            children: mergeTreeLevel(key, { inDeletedAncestor: 'base' }),
                        });
                    } else {
                        pushItems([headItem]);
                    }
                } else {
                    // Delete it because it was ignored in head branch
                    logger.debug(
                        'Deleted as deleted in base and not modified in head',
                        key,
                        'in',
                        parent
                    );
                    changes.set(key, { case: 'deleted-in-base', ancestor: ancestorItem });
                }
            } else if (!ancestorItem && baseItem) {
                // Item was created in base branch
                logger.debug('Created in base', key, 'in', parent);
                assertIsUndefined(headItem);

                if (headNode) {
                    assertIsDefined(ancestorNode);
                    // The node exists somewhere else in the head tree, it means it was moved
                    pushItemModification(key, ancestorNode.item, baseItem, headNode.item);
                } else {
                    pushItemChange(strategy.setChildren(baseItem, mergeTreeLevel(key)), {
                        case: 'added-in-base',
                        base: baseItem,
                    });
                }
            } else if (!ancestorItem && headItem) {
                // Item was created in head branch
                logger.debug('Created in head', key, 'in', parent);
                assertIsUndefined(baseItem);

                if (baseNode) {
                    assertIsDefined(ancestorNode);
                    // The node exists somewhere else in the base tree, it means it was moved
                    pushItemModification(key, ancestorNode.item, baseNode.item, headItem);
                } else {
                    pushItemChange(strategy.setChildren(headItem, mergeTreeLevel(key)), {
                        case: 'added-in-head',
                        head: headItem,
                    });
                }
            } else {
                throw new Error(`Unexpected merge case for: ${key}`);
            }
        }

        logger.debug('return in', parent, items);
        return items;
    };

    // Start merging from the top level
    const topItems = mergeTreeLevel(undefined);

    return {
        items: topItems,
        changes,
    };
}

/**
 * Default merge items strategy. It can be used as a reference for custom merge strategies.
 * It defaults to choosing the head version of the item and does not generate conflicts.
 */
export function defaultMergeConflictsStrategy<Item>(
    mergeCase: MergeItemsConflictCase<Item>,
    strategy: MergeStrategy<Item>
): { items: Item[] } {
    switch (mergeCase.case) {
        case 'conflict-both-modified':
            return {
                // Choose head over base, but use merged children
                items: [strategy.setChildren(mergeCase.head, mergeCase.children)],
            };
        case 'conflict-deleted-in-head':
            return {
                // Preserve base item, as it was modified in head branch
                items: [strategy.setChildren(mergeCase.base, mergeCase.children)],
            };
        case 'conflict-deleted-in-base':
            return {
                // Preserve head item, as it was modified in base branch
                items: [strategy.setChildren(mergeCase.head, mergeCase.children)],
            };
        default:
            assertNever(mergeCase);
    }
}

/**
 * Test if a change in a merge is a conflict case.
 */
export function isMergeConflictCase<Item>(
    change: MergeItemsCase<Item>
): change is MergeItemsConflictCase<Item> {
    return (
        change.case === 'conflict-both-modified' ||
        change.case === 'conflict-deleted-in-base' ||
        change.case === 'conflict-deleted-in-head'
    );
}

/**
 * Compare if 2 trees are equal. Returns true if they are equal, false otherwise.
 */
function compareTrees<Item>(a: Item[], b: Item[], strategy: MergeStrategy<Item>): boolean {
    if (a.length !== b.length) {
        return false;
    }

    for (let index = 0; index < a.length; index++) {
        const aItem = a[index];
        const bItem = b[index];

        // we can safely assume that a and b have the same length
        // and that a[index] and b[index] are defined in this loop
        assertIsDefined(aItem);
        assertIsDefined(bItem);

        if (strategy.getId(aItem) !== strategy.getId(bItem)) {
            return false;
        }

        if (strategy.compareItems(aItem, bItem) !== 0) {
            return false;
        }

        const aChildren = strategy.getChildren(aItem);
        const bChildren = strategy.getChildren(bItem);

        if (!compareTrees(aChildren, bChildren, strategy)) {
            return false;
        }
    }

    return true;
}
